import { Autocomplete, TextField, Select, MenuItem, FormControl, Checkbox, FormControlLabel } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18next from "i18next";
import { FC, useCallback, useEffect, useState } from "react";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import MaskedInput from "react-text-mask";
import { toast } from "react-toastify";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import Collaborator from "../@types/Collaborator";
import CostCenter from "../@types/CostCenter";
import { AdvancePayment } from "../@types/AdvancePayment";
import { Currency, currencyLabels } from "../enums/Currency";
import { fetchClients, fetchCostCenters } from "../service/costCenter";
import { fetchAdvancePayment, useDeleteReportEntry } from "../service/reportEntry";
import { addReportEntry, updateReportEntry } from "../service/reportEntry";
import FormPage from "../templates/FormPage";
import Loading from "./atoms/Loading";
import { Status } from "../enums/Status";
import Modal from "./atoms/Modal";

interface AdvancePaymentFormProps {
  account: Collaborator;
}

const AdvancePaymentForm: FC<AdvancePaymentFormProps> = ({ account }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<AdvancePayment>({
    defaultValues: {
      isAdvance: true,
      isForCurrencyConversion: false,
      expenseDate: new Date().toISOString().split("T")[0],
    },
  });
  const { mutate: deleteReportEntry, isPending: isDeleting } = useDeleteReportEntry();

  const [clients, setClients] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [selectedCostCenter, setSelectedCostCenter] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [dataLoaded] = useState(true);
  const [status, setStatus] = useState<string>();
  const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const isEdit = !!id;

  const watchCostCenterClient = watch("costCenter.client", "");
  const watchIsForCurrencyConversion = watch("isForCurrencyConversion", false);

  const defaultMaskOptions = {
    prefix: "R$ ",
    suffix: "",
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ".",
    allowDecimal: true,
    decimalSymbol: ",",
    decimalLimit: 2,
    integerLimit: 12,
    allowNegative: false,
    allowLeadingZeroes: false,
  };
  const mask = createNumberMask(defaultMaskOptions);

  const mutation = useMutation(
    async (data: FieldValues) => {
      const costCenter = {
        id: data.costCenter.id,
      };
      const collaborator = {
        id: account.id,
      };

      const mappedData = {
        ...data,
        costCenter,
        collaborator,
        isAdvance: true,
        isForCurrencyConversion: !!data.isForCurrencyConversion,
        targetCurrency: data.isForCurrencyConversion ? data.targetCurrency : Currency.BRL,
        value: data.requestedAmount,
        description: data.additionalComments || i18next.t("advancePaymentForm.defaultDescription"),
        expendCategory: "ADVANCE",
      };

      if (id) {
        return updateReportEntry({ ...mappedData, id: Number(id) } as any);
      }
      return addReportEntry(mappedData as any, []);
    },
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries(["reportEntries"]);
        if (result.warnings && result.warnings.length > 0) {
          toast.warning(result.warnings[0], {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        toast.success(i18next.t("advancePaymentForm.toast.createSuccess"));
        navigate("/");
      },
      onError: (error: Error) => {
        console.error("Erro na mutação:", error.message);
        toast.error(error.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      },
    },
  );

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (selectedClient) {
      localStorage.setItem("selectedClient", selectedClient);
    }
    if (data.costCenter.id) {
      localStorage.setItem("selectedProject", data.costCenter.id.toString());
    }
    await mutation.mutate(data);
  };

  const handleFetchCostCenter = useCallback(async (client: string) => {
    setLoading(true);
    try {
      const data = await fetchCostCenters(client);
      setCostCenters(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error(i18next.t("advancePaymentForm.toast.fetchProjectsError"), {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      reset({
        isAdvance: true,
        isForCurrencyConversion: false,
        expenseDate: new Date().toISOString().split("T")[0],
      });
      setSelectedClient("");
      setSelectedCostCenter(undefined);
    }

    if (id) {
      fetchAdvancePayment(Number(id)).then((data) => {
        setStatus(data.reportEntry.reportEntryStatus);
        reset({
          isAdvance: true,
          isForCurrencyConversion: data.isForCurrencyConversion,
          expenseDate: data.reportEntry.expenseDate,
          requestedAmount: data.requestedAmount,
          targetCurrency: data.targetCurrency as Currency,
          additionalComments: data.additionalComments,
          costCenter: {
            id: data.reportEntry.costCenter.id,
            name: data.reportEntry.costCenter.name,
            client: data.reportEntry.costCenter.client,
          },
          collaborator: {
            id: data.reportEntry.collaborator.id,
            name: data.reportEntry.collaborator.name,
          },
        });
      });
    }
  }, [id, reset]);

  useEffect(() => {
    fetchClients().then((data) => {
      setClients(data);
    });
  }, []);

  useEffect(() => {
    if (selectedClient) {
      handleFetchCostCenter(selectedClient);
    }
  }, [selectedClient, handleFetchCostCenter]);

  useEffect(() => {
    if (watchCostCenterClient) {
      handleFetchCostCenter(watchCostCenterClient);
    }
  }, [handleFetchCostCenter, watchCostCenterClient]);

  return (
    <FormPage>
      <h1 className="text-vcolor-hover p-5 underline">
        {i18next.t(id ? "advancePaymentForm.title.edit" : "advancePaymentForm.title.create")}
      </h1>

      {id && !dataLoaded ? (
        <Loading />
      ) : (
        <div className="flex justify-center items-center pb-2">
          <form
            className="flex flex-col items-center w-1/2 my-4 laptop-max:w-full max-w-screen-md overflow-hidden"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Caixa de Aviso */}
            <div className="w-full mb-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg shadow-sm">
              <div className="flex items-start p-4 gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">
                    {i18next.t("advancePaymentForm.info.title")}
                  </h3>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    {i18next.t("advancePaymentForm.info.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Cliente e Centro de Custo */}
            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-1/2 pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="cliente"
                  className={`block ${
                    errors.costCenter?.id && !selectedClient ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.client")}
                  <span className="text-red-500">*</span>
                </label>
                <Autocomplete
                  id="client"
                  options={clients}
                  value={watchCostCenterClient || selectedClient}
                  onChange={(event, newValue) =>
                    setSelectedClient(newValue || (event.target as HTMLInputElement).value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.costCenter?.id}
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "36px",
                          "& input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        },
                      }}
                      className={`w-full border ${
                        errors.costCenter?.id && !selectedClient
                          ? "border-red-500"
                          : "border-gray-300 dark:border-gray-400"
                      } text-gray-500 text-sm rounded-md block dark:bg-white dark:text-gray-600`}
                    />
                  )}
                />
              </div>

              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pl-0">
                <label
                  htmlFor="projeto"
                  className={`block ${
                    errors.costCenter?.id && !selectedCostCenter ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.project")}
                  <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="costCenter.id"
                  control={control}
                  defaultValue={selectedCostCenter}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      id="project"
                      options={loading ? [] : costCenters}
                      getOptionLabel={(option) => option.name}
                      value={costCenters.find((costCenter) => costCenter.id === field.value) || null}
                      onChange={(_event, newValue) => field.onChange(newValue ? newValue.id : null)}
                      slotProps={{
                        paper: {
                          sx: {
                            "& .MuiAutocomplete-option": {
                              textAlign: "left",
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.costCenter?.id}
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "36px",
                              "& input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            },
                          }}
                          className={`w-full border ${
                            errors.costCenter?.id && !field.value
                              ? "border-red-500"
                              : "border-gray-300 dark:border-gray-400"
                          } text-gray-500 text-sm rounded-md block dark:bg-white dark:text-gray-600`}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </div>

            {/* Data do Adiantamento */}
            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="expenseDate"
                  className={`block ${
                    errors.expenseDate ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("advancePaymentForm.fields.expenseDate.label")}
                  <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    {...register("expenseDate", { required: true })}
                    className={`w-full h-[36px] border transition-colors duration-200 ${
                      errors.expenseDate
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500"
                    } text-gray-700 rounded-lg block text-base px-2 py-1 dark:bg-white dark:text-gray-600 
                    hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1`}
                  />
                  {errors.expenseDate && (
                    <p className="mt-1 text-sm text-left text-red-500">
                      {i18next.t("advancePaymentForm.fields.expenseDate.required")}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pl-0">
                <label
                  htmlFor="requestedAmount"
                  className={`block ${
                    errors.requestedAmount ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("advancePaymentForm.fields.requestedAmount.label")}
                  <span className="text-red-500">*</span>
                </label>
                <div>
                  <MaskedInput
                    mask={mask}
                    type="text"
                    defaultValue={watch("requestedAmount") || ""}
                    className={`w-full h-[36px] bg-white p-1 border transition-colors duration-200 ${
                      errors.requestedAmount
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500"
                    } text-gray-700 text-base rounded-lg block pl-4 dark:bg-white dark:text-gray-600
                    hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2`}
                    {...register("requestedAmount", {
                      required: true,
                      validate: (value) => {
                        const stringValue = String(value);
                        const numericValue = parseFloat(
                          stringValue
                            .replace(/[^0-9.,-]/g, "")
                            .replace(/\./g, "")
                            .replace(/,/g, "."),
                        );
                        return (
                          (!isNaN(numericValue) && numericValue > 0) ||
                          i18next.t("advancePaymentForm.fields.requestedAmount.greaterThanZero")
                        );
                      },
                    })}
                    onChange={(e) => {
                      setValue(
                        "requestedAmount",
                        parseFloat(
                          e.target.value
                            .replace(/[^0-9.,-]/g, "")
                            .replace(/\./g, "")
                            .replace(/,/g, "."),
                        ),
                      );
                    }}
                  />
                  {errors.requestedAmount && (
                    <p className="mt-1 text-sm text-left text-red-500">
                      {errors.requestedAmount.message ||
                        i18next.t("advancePaymentForm.fields.requestedAmount.required")}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Conversão de Moeda */}
            <div className="flex flex-col w-full mb-4 laptop-max:w-[80%]">
              <FormControlLabel
                control={
                  <Checkbox
                    {...register("isForCurrencyConversion")}
                    checked={watchIsForCurrencyConversion}
                    onChange={(e) => setValue("isForCurrencyConversion", e.target.checked)}
                  />
                }
                label={i18next.t("advancePaymentForm.fields.currencyConversion.label")}
                className="text-color-default-gray text-sm font-semibold"
              />
            </div>

            {/* Moeda de Destino - Mostrado apenas se conversão estiver marcada */}
            {watchIsForCurrencyConversion && (
              <div className="flex flex-col w-full mb-4 laptop-max:w-[80%]">
                <label
                  htmlFor="targetCurrency"
                  className={`block ${
                    errors.targetCurrency ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("advancePaymentForm.fields.targetCurrency.label")}
                  <span className="text-red-500">*</span>
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    id="targetCurrency"
                    value={watch("targetCurrency") || ""}
                    {...register("targetCurrency", {
                      required: watchIsForCurrencyConversion,
                    })}
                    error={!!errors.targetCurrency}
                    className="bg-white"
                    sx={{
                      height: "36px",
                      "& .MuiSelect-select": {
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        padding: "0px 12px",
                      },
                    }}
                  >
                    <MenuItem value="">{i18next.t("advancePaymentForm.fields.targetCurrency.placeholder")}</MenuItem>
                    {Object.values(Currency).map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currencyLabels[currency]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {errors.targetCurrency && (
                  <p className="mt-1 text-sm text-left text-red-500">
                    {i18next.t("advancePaymentForm.fields.targetCurrency.required")}
                  </p>
                )}
              </div>
            )}

            {/* Comentários Adicionais */}
            <div className="flex flex-col w-full mb-4 laptop-max:w-[80%]">
              <label
                htmlFor="additionalComments"
                className="block text-color-default-gray text-sm font-semibold text-left mb-1"
              >
                {i18next.t("form.additionalComments")}
              </label>
              <textarea
                id="additionalComments"
                {...register("additionalComments")}
                rows={3}
                placeholder={i18next.t("advancePaymentForm.fields.additionalComments.placeholder")}
                className="w-full px-3 py-2 border rounded-md transition-colors duration-200 border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500 bg-white text-base hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2"
              />
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-row w-full gap-2 justify-end laptop-max:flex-col laptop-max:items-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={mutation.isLoading}
                className="w-[20%] laptop-max:w-[80%] h-10 border border-vcolor bg-white text-vcolor rounded-md 
                transition-colors duration-150 ease-in-out hover:bg-vcolor/10
                focus:outline-none focus:ring-1 focus:ring-vcolor focus:ring-opacity-30
                disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {i18next.t("back")}
              </button>

              {isEdit &&
                status !== Status.PAID &&
                status !== Status.APPROVED &&
                status !== Status.FINANCIAL_VALIDATION && (
                  <button
                    type="button"
                    disabled={isDeleting}
                    className="w-[30%] mb-2 inline-flex justify-center rounded-md border border-red-700 bg-transparent px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    onClick={() => {
                      setIsOpenDelete(true);
                    }}
                  >
                    {i18next.t("delete")}
                  </button>
                )}

              {(!isEdit ||
                (status !== Status.PAID && status !== Status.APPROVED && status !== Status.FINANCIAL_VALIDATION)) && (
                <button
                  type="submit"
                  disabled={mutation.isLoading}
                  className="w-[30%] laptop-max:w-[80%] h-10 bg-vcolor text-white rounded-md
                transition-colors duration-150 ease-in-out hover:bg-vcolor/90
                focus:outline-none focus:ring-1 focus:ring-vcolor focus:ring-opacity-30
                disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isPending
                    ? i18next.t("saving")
                    : i18next.t(isEdit ? "advancePaymentForm.actions.edit" : "advancePaymentForm.actions.create")}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
      <Modal
        title={i18next.t("advancePaymentForm.deleteModal.title")}
        isOpen={isOpenDelete}
        setIsOpen={setIsOpenDelete}
        body={
          <div className="mt-2">
            <p className="text-sm text-gray-500">{i18next.t("advancePaymentForm.deleteModal.text")}</p>
          </div>
        }
        bottom={
          <div className="flex flex-row justify-around mt-4">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => {
                deleteReportEntry(Number(id));
                setIsOpenDelete(false);
              }}
            >
              {i18next.t("advancePaymentForm.deleteModal.confirm")}
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => setIsOpenDelete(false)}
            >
              {i18next.t("advancePaymentForm.deleteModal.cancel")}
            </button>
          </div>
        }
      />
    </FormPage>
  );
};

export default AdvancePaymentForm;
